import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Leaf, 
  ArrowRight, 
  Recycle, 
  Lightbulb, 
  Beaker,
  TrendingUp,
  MapPin
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { OpportunitySection } from '../components/landing/OpportunitySection';
import { CircularProcess } from '../components/landing/CircularProcess';

const SOLUTION_ITEMS = [
  { icon: Recycle, title: 'Waste as Resource', description: 'Transform local waste streams into valuable feedstock for new materials' },
  { icon: Leaf, title: 'Bio-Based Innovation', description: 'Leverage biological processes to create biodegradable alternatives' },
  { icon: TrendingUp, title: 'Scalable Impact', description: 'Build economically viable circular supply chains for urban ecosystems' },
];

const WHY_ITEMS = [
  { icon: Beaker, title: 'Science-Backed', description: 'Rigorous R&D and biology-driven innovation' },
  { icon: Leaf, title: 'Truly Circular', description: 'Closed-loop systems from waste to biodegradation' },
  { icon: MapPin, title: 'Local Impact', description: 'Sourced and scaled within urban ecosystems' },
  { icon: TrendingUp, title: 'Economically Viable', description: 'Sustainable solutions that make business sense' },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#A8C9A5] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4A574] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto max-w-7xl relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 min-w-0">
              <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none">
                <Leaf className="w-3 h-3 mr-1" />
                Next Gen Bio-Materials
              </Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#2C3E2F] leading-tight">
                Transforming Urban Waste Into
                <span className="text-[#D4A574]"> Circular Materials</span>
              </h1>
              <p className="text-base md:text-lg text-[#5A6B5E] leading-relaxed">
                We build high-performance biomaterials from waste.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#D4A574] hover:bg-[#C09460] text-white w-full sm:w-auto">
                    Collaborate With Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-[#A8C9A5] text-[#4A5D4E] hover:bg-[#F5F3EE] w-full sm:w-auto">
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/e92frzmb_mycelium-free-use-AdobeStock.jpeg" 
                alt="Mycelium Biomaterial" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <OpportunitySection />

      {/* Solution Section */}
      <section className="py-20 px-6 bg-[#4A5D4E] text-white">
        <div className="container mx-auto max-w-6xl text-center">
          <Badge className="bg-white/20 text-white border-none mb-4">
            Our Solution
          </Badge>
          <h2 className="text-4xl font-bold mb-6">
            Closing the Loop with Biology
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-12">
            We believe waste is misplaced resource. Biology is the most advanced materials lab on Earth. 
            Circularity must be local, scalable, and economically viable.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {SOLUTION_ITEMS.map((item) => (
              <div key={item.title} className="bg-white/10 backdrop-blur-sm rounded-xl p-8 hover:bg-white/15 transition-colors">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/80">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CircularProcess />

      {/* Why Punarvibes Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3E2F] mb-4">
              Why Choose Punarvibes
            </h2>
            <p className="text-lg text-[#5A6B5E] max-w-3xl mx-auto">
              We bring together scientific rigor, environmental commitment, and circular economy principles.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_ITEMS.map((item) => (
              <Card key={item.title} className="border-[#DDD5C7] text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-16 h-16 bg-[#A8C9A5]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-[#4A5D4E]" />
                  </div>
                  <CardTitle className="text-[#2C3E2F] text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#5A6B5E]">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section className="py-20 px-6 bg-[#FDFBF7]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/nql7s4sq_WhatsApp%20Image%202026-02-17%20at%206.47.25%20PM.jpeg" 
                    alt="Researcher working with mycelium in lab" 
                    className="rounded-xl shadow-lg w-full h-72 object-cover object-center"
                  />
                </div>
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/j0ot7tr2_3.jpeg" 
                    alt="Raw materials - coir and textile waste feedstock" 
                    className="rounded-xl shadow-lg w-full h-48 object-cover"
                  />
                </div>
                <div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_biofab-labs/artifacts/cpetbvag_5.jpeg" 
                    alt="Mycelium growth close-up" 
                    className="rounded-xl shadow-lg w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
            <div>
              <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none mb-4">
                <Lightbulb className="w-3 h-3 mr-1" />
                Research & Innovation
              </Badge>
              <h2 className="text-4xl font-bold text-[#2C3E2F] mb-6">
                Advancing Material Science
              </h2>
              <p className="text-lg text-[#5A6B5E] mb-8">
                At Punarvibes, we develop biomaterials using mycelium-based binding systems, 
                waste-based feedstock, and controlled biological manufacturing. Our focus is on 
                creating practical, biodegradable alternatives to conventional materials using 
                locally available waste streams.
              </p>
              
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="border border-[#DDD5C7] rounded-lg px-6">
                  <AccordionTrigger className="text-[#2C3E2F] hover:text-[#4A5D4E]">
                    Current Research Focus
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5A6B5E]">
                    Mycelium-based natural binders, improved processing of agricultural residues, 
                    textile waste reuse methods, and lifecycle analysis of biodegradable materials.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" className="border border-[#DDD5C7] rounded-lg px-6">
                  <AccordionTrigger className="text-[#2C3E2F] hover:text-[#4A5D4E]">
                    Technology Readiness
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5A6B5E]">
                    Currently at TRL 3 (experimental proof of concept) for both acoustic panels and 
                    biosorbents, we are working toward developing laboratory-scale prototypes for 
                    structured validation and performance testing.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3" className="border border-[#DDD5C7] rounded-lg px-6">
                  <AccordionTrigger className="text-[#2C3E2F] hover:text-[#4A5D4E]">
                    Collaboration Opportunities
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5A6B5E]">
                    We welcome partnerships with research institutions, material scientists, 
                    architecture firms, and sustainability-focused organizations.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#4A5D4E] text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Build a Circular Future?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Partner with us on pilot projects, research collaborations, or simply explore 
            how circular materials can transform your sustainability strategy.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/products">
              <Button size="lg" className="bg-[#D4A574] hover:bg-[#C09460] text-white">
                Explore Products
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
